import "./ViewJob.scss";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import JobService from "../../services/JobService";
import {
  JobOrder,
  ContainerStats,
  TripContainer,
  JobBreakUpDetail,
  JobStatusEnum,
} from "../../interfaces/job.interface";
import { useLoader } from "../../contexts/LoaderContext";
import { useLoading } from "../../contexts/LoadingContext";
import JobDetails from "../../components/Jobs/JobDetails";
import ContainerType from "../../components/ContainerType/ContainerType";
import ContainerStatus from "../../components/ContainerStatus/ContainerStatus";
import {
  formatIsoDate,
  getContainerStatsFromJobOrder,
  transformTruckNumber,
  containerSizeStringToNumber,
} from "../../utils/general";
import TableComponent, {
  TableColumn,
  highlightText,
} from "../../components/TableComponent/TableComponent";
import StatsBoxContainer from "../../components/StatsBoxContainer/StatsBoxContainer";
import TransporterDetails from "../../components/TransporterDetails";
import { SelectChangeEvent } from "@mui/material";
import { showToast } from "../../components/Toaster";
import Modal from "../../components/Modal/Modal";
import SearchBar from "../../components/SearchBar/SearchBar";
import { generateExcelSheet } from "../../utils/generateExcelSheet";
import { handleExcelFileUpload } from "../../utils/importContainerDataFromExcel";
import { Container } from "../../interfaces/container.interface";
import JobBreakupTable from "../../components/Jobs/JobBreakupTable";
import JobStatus from "../../components/Jobs/JobStatus";
import LiveTracking from "../../components/LiveTracking/LiveTracking";
import UpdateContainerModal from "../../components/Jobs/UpdateContainerModal";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import JobCompletionStatus from "../../components/Jobs/JobCompletionStatus";
import { ReactComponent as EditIcon } from "../../assets/icons/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete-icon.svg";
import { ReactComponent as CancelIcon } from "../../assets/icons/cancel.svg";
import ButtonDropdown from "../../components/ButtonDropdown/ButtonDropdown";
import TripService from "../../services/TripService";
import NAValue from "../../components/NAValue/NAValue";
import UpdateTruckModal from "../../components/Jobs/UpdateTruckModal";
import ForceEndTripModal from "../../components/Jobs/ForceEndTripModal";

interface EditContainerData {
  jobBreakupDetailId: number;
  containerNumber: string;
  containerSize: number;
  containerType: string;
  driverIdName: string;
  truckIdName: string;
  transporterIdName: string;
  jobCode: string;
  source: string;
  destination: string;
}

interface ParsedData {
  containerNumber: string;
  containerSize: number;
  containerType: string;
}

const ViewJob: React.FC = () => {
  const { jobId, jobCode } = useParams<{ jobId: string; jobCode: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobOrder, setJobOrder] = useState<JobOrder | null>(null);
  const [activeTab, setActiveTab] = useState("details");
  const { showLoader, hideLoader } = useLoader();
  const loader = useLoading();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const pageSizeOptions = [5, 8, 10, 20, 50, 100];
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editContainerData, setEditContainerData] =
    useState<EditContainerData | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isImportContainerModalOpen, setIsImportContainerModalOpen] =
    useState(false);
  const [uploadedData, setUploadedData] = useState<Container[]>([]);
  const [validUploadedData, setValidUploadedData] = useState<Container[]>([]);
  const [uploadedJobCode, setUploadedJobCode] = useState("");
  const [existingContainerNumbers, setExistingContainerNumbers] = useState<
    Set<string>
  >(new Set());
  const [duplicateContainerNumbers, setDuplicateContainerNumbers] = useState<
    Set<string>
  >(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [containerStats, setContainerStats] = useState<ContainerStats>({
    totalContainer: 0,
    totalAssigned: 0,
    pendingToAssign: 0,
    totalDroppedContainers: 0,
    totalJobStatusPercentage: 0,
    totalPickedContainers: 0,
    totalUnpickedContainers: 0,
    totalCancelledContainers: 0,
    totalBalanceContainers: 0,
  });
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [selectedJobBreakupDetailId, setSelectedJobBreakupDetailId] = useState<
    number | null
  >(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<Record<
    string,
    any
  > | null>(null);
  const [openDispatchConfirm, setOpenDispatchConfirm] = useState(false);
  const [openJobStatusChangeConfirm, setOpenJobStatusChangeConfirm] =
    useState(false);
  const [newJobStatus, setNewJobStatus] = useState("");
  const [jobStatusChangeTitle, setJobStatusChangeTitle] = useState("");
  const [isForceEndTripModalOpen, setIsForceEndTripModalOpen] = useState(false);
  const [selectedTripToEnd, setSelectedTripToEnd] = useState<{
    jobBreakupDetailId: number;
    jobCode: string;
    containerNumber: string;
    containerSiTy: string;
    truckIdName: string;
    driverIdName: string;
    transporterIdName: string;
    pickedAt: string;
    tripStatus: string;
    source: string;
    destination: string;
  } | null>(null);
  const [isTruckModalOpen, setIsTruckModalOpen] = useState(false);
  const [editTruckData, setEditTruckData] = useState<{
    jobBreakupDetailId: number;
    currentTruckNumber: string;
    driverId: number;
    transporterId: number;
    driverName: string;
    transporterName: string;
  } | null>(null);

  // Extract container parameter from URL and set search query
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const containerParam = searchParams.get("container");

    // Check if URL path ends with "/containers"
    const pathEndsWithContainers = location.pathname.endsWith("/containers");
    const pathIncludesContainers = location.pathname.includes("/containers");

    if (containerParam) {
      setActiveTab("containers");
      setSearchQuery(containerParam);
    } else if (pathEndsWithContainers || pathIncludesContainers) {
      setActiveTab("containers");
    } else {
      // Reset search query if container parameter is removed
      setSearchQuery("");
    }
  }, [location.search, location.pathname]);

  // Memoize the fetch function to avoid recreating it on every render
  const fetchJobDetails = useCallback(
    async (suppressLoader = false) => {
      if (!jobId) {
        setError("No job ID provided");
        setLoading(false);
        return;
      }

      try {
        if (!suppressLoader) {
          showLoader();
        }
        setLoading(true);
        setError(null);
        const jId = parseInt(jobId);
        const response = await JobService.getJobDetails(jId);

        if (!response.data.error) {
          setJobOrder(response.data.data);
          setContainerStats(getContainerStatsFromJobOrder(response.data.data));
        } else {
          console.error("Error in fetching job details");
          setError("Failed to load job details");
        }
      } catch (err: any) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
        if (!suppressLoader) {
          hideLoader();
        }
      }
    },
    [jobId, showLoader, hideLoader]
  );

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1); // Reset to first page when page size changes
    },
    []
  );

  // Memoize the container data calculations
  const containerData = useMemo(() => {
    if (!jobOrder) return [];

    return jobOrder.jobBreakups.flatMap((breakup) =>
      breakup.jobBreakUpDetails.map((detail) => ({
        jobBreakupDetailId: detail.jobBreakupDetailId,
        containerNumber: detail.containerNumber || "N/A",
        containerSize: detail.containerSize || "N/A",
        containerType: detail.containerType || "N/A",
        containerSiTy:
          detail.containerSize && detail.containerType
            ? detail.containerSize + " " + detail.containerType
            : "N/A",
        truckIdName: detail.truckIdName?.name
          ? transformTruckNumber(detail.truckIdName.name)
          : "N/A",
        driverIdName: detail.driverIdName?.name || "N/A",
        transporterIdName: detail.transporterIdName?.name || "N/A",
        pickedAt: detail.startDate ? formatIsoDate(detail.startDate) : "N/A",
        droppedAt: detail.endDate ? formatIsoDate(detail.endDate) : "N/A",
        tripStatus: detail.tripStatus || "N/A",
        jobCode: jobOrder.jobCode || "N/A",
        jobId: jobOrder.jobId || "N/A",
        source: jobOrder.source || "N/A",
        destination: jobOrder.destination || "N/A",
      }))
    );
  }, [jobOrder]);

  // Memoize the filtered data
  const filteredContainerData = useMemo(() => {
    if (!searchQuery) return containerData;

    const searchLower = searchQuery.toLowerCase();
    return containerData.filter((item) => {
      return (
        (item.containerNumber?.toLowerCase() || "").includes(searchLower) ||
        (item.containerSize?.toString() || "").includes(searchLower) ||
        (item.containerType?.toLowerCase() || "").includes(searchLower) ||
        (item.truckIdName?.toLowerCase() || "").includes(searchLower) ||
        (item.driverIdName?.toLowerCase() || "").includes(searchLower) ||
        (item.transporterIdName?.toLowerCase() || "").includes(searchLower) ||
        (item.tripStatus?.toLowerCase() || "").includes(searchLower) ||
        (item.jobBreakupDetailId?.toString() || "").includes(searchLower) ||
        (item.jobCode?.toLowerCase() || "").includes(searchLower) ||
        (item.jobId?.toString() || "").includes(searchLower)
      );
    });
  }, [containerData, searchQuery]);

  // Memoize paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filteredContainerData.slice(startIndex, endIndex);

    return {
      data: paginatedItems.map((item, index) => ({
        ...item,
        serialNumber: startIndex + index + 1,
      })),
      totalCount: filteredContainerData.length,
    };
  }, [filteredContainerData, currentPage, pageSize]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(filteredContainerData.length / pageSize);
  }, [filteredContainerData.length, pageSize]);

  const handleRefresh = async () => {
    try {
      loader.setLoading(true);
      await fetchJobDetails(true);
    } finally {
      setTimeout(() => {
        loader.setLoading(false);
      }, 1000);
    }
  };

  const handleAssignContainers = () => {
    setIsAssignModalOpen(true);
  };

  const handleUpdatedJob = (updatedJob: JobOrder) => {
    setJobOrder(updatedJob);
    setContainerStats(getContainerStatsFromJobOrder(updatedJob));
  };

  const downloadExcelTemplate = async () => {
    const headers = ["Job Code", "Container Number", "Size", "Type"];
    const data = [
      {
        "Job Code": jobCode,
      },
    ];
    loader.setLoading(true);
    try {
      await generateExcelSheet(headers, data, {
        fileName: `${jobCode}.xlsx`,
        download: true,
      });
      showToast(`Downloaded ${jobCode}.xlsx`, "success");
    } catch (error) {
      showToast(`Download ${jobCode}.xlsx failed`, "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const handleOpenImportContainerDataModal = () => {
    setIsImportContainerModalOpen(true);
  };

  const handleCloseImportContainerDataModal = () => {
    setIsImportContainerModalOpen(false);
    setUploadedData([]);
    setValidUploadedData([]);
    setUploadedJobCode("");
  };

  const isIntegrateButtonEnabled = () => {
    return (
      uploadedJobCode.trim() !== "" &&
      uploadedJobCode === jobCode?.trim().replace(/#/g, "").toUpperCase()
    );
  };

  const readSelectedExcel = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      loader.setLoading(true);
      setUploadError(null);

      const fileType = file.name.split(".").pop()?.toLowerCase();
      console.log("File type:", fileType);

      if (fileType === "xlsx" || fileType === "xls" || fileType === "csv") {
        await handleExcelFileUpload(
          file,
          (result) => {
            console.log("Parsed Excel data:", result);

            if (result.jobCode) {
              const parsedJobCode = result.jobCode
                .trim()
                .replace(/#/g, "")
                .toUpperCase();
              setUploadedJobCode(parsedJobCode);

              // Get all container numbers from job breakup details for duplication check
              const existingContainers = new Set<string>();
              jobOrder?.jobBreakups.forEach((breakup) => {
                breakup.jobBreakUpDetails.forEach((detail) => {
                  if (detail.containerNumber) {
                    existingContainers.add(
                      detail.containerNumber.toUpperCase()
                    );
                  }
                });
              });
              setExistingContainerNumbers(existingContainers);

              // Track duplicates within the uploaded file
              const usedContainerNumbers = new Set<string>();
              const duplicates = new Set<string>();
              result.data.forEach((container) => {
                const containerNumber = container.containerNumber.toUpperCase();
                if (usedContainerNumbers.has(containerNumber)) {
                  duplicates.add(containerNumber);
                }
                usedContainerNumbers.add(containerNumber);
              });
              setDuplicateContainerNumbers(duplicates);

              // Convert all containers to uppercase and set in state
              const allContainers = result.data.map((container) => ({
                containerNumber: container.containerNumber.toUpperCase(),
                containerSize: container.containerSize,
                containerType: container.containerType.toUpperCase(),
              }));

              setUploadedData(allContainers);
              setValidUploadedData(allContainers);
            }
          },
          (error) => {
            setUploadError(error);
            showToast(error, "error");
          }
        );
      } else {
        setUploadError("Unsupported file format");
        showToast("Unsupported file format", "error");
      }
    } catch (error) {
      setUploadError("Error reading file");
      showToast("Error reading file", "error");
      console.error("Error reading file:", error);
    } finally {
      loader.setLoading(false);
    }
  };

  // Function to generate trip container list from valid uploaded data
  function generateTripContainerList(): TripContainer[] {
    if (!jobOrder?.jobBreakups || !validUploadedData.length) {
      console.log("No job breakups or valid uploaded data");
      return [];
    }

    const tripContainers: TripContainer[] = [];
    const usedContainerNumbers = new Set<string>();

    // First, collect all unassigned job breakup details
    const unassignedDetails = jobOrder.jobBreakups.flatMap((breakup) =>
      breakup.jobBreakUpDetails.filter((detail) => !detail.containerNumber)
    );

    console.log("Unassigned Details:", unassignedDetails);
    console.log("Valid Uploaded Data:", validUploadedData);

    // For each unassigned detail, find a matching container
    unassignedDetails.forEach((detail) => {
      // Find a matching container that hasn't been used yet
      const matchingContainer = validUploadedData.find((container) => {
        // Validate container number format (4 letters followed by 7 numbers)
        const isValidFormat = /^[A-Z]{4}\d{7}$/.test(container.containerNumber);
        if (!isValidFormat) {
          console.log(
            `Skipping invalid container format: ${container.containerNumber}`
          );
          return false;
        }

        const detailSize = containerSizeStringToNumber(detail.containerSize);
        const containerSize = container.containerSize;
        const sizeMatch = detailSize === containerSize;
        const typeMatch =
          detail.containerType.toUpperCase() === container.containerType;
        const notUsed = !usedContainerNumbers.has(container.containerNumber);
        const notExisting = !existingContainerNumbers.has(
          container.containerNumber
        );
        const notDuplicate = !duplicateContainerNumbers.has(
          container.containerNumber
        );

        console.log("Matching attempt:", {
          detailSize,
          containerSize,
          sizeMatch,
          typeMatch,
          notUsed,
          notExisting,
          notDuplicate,
          containerNumber: container.containerNumber,
        });

        return sizeMatch && typeMatch && notUsed && notExisting && notDuplicate;
      });

      if (matchingContainer) {
        usedContainerNumbers.add(matchingContainer.containerNumber);
        tripContainers.push({
          tripId: detail.jobBreakupDetailId,
          containerNumber: matchingContainer.containerNumber,
        });
      }
    });

    console.log("Generated Trip Containers:", tripContainers);
    return tripContainers;
  }

  const handleSave = async () => {
    const tripList = generateTripContainerList();
    console.log("Trip List for Assignment:", tripList);

    if (tripList.length > 0) {
      try {
        loader.setLoading(true);
        const res = await JobService.assignContainers(tripList);
        const updatedJob = res.data.data;
        showToast(
          `Successfully assigned ${tripList.length} containers to Job ${jobCode}`,
          "success"
        );
        setJobOrder(updatedJob);
        setContainerStats(getContainerStatsFromJobOrder(updatedJob));
        handleCloseImportContainerDataModal();
      } catch (error) {
        console.error("Error assigning containers", error);
        showToast(
          `Failed to assign the containers, please try again!`,
          "error"
        );
      } finally {
        loader.setLoading(false);
      }
    } else {
      showToast(
        "No valid containers to assign. Please check if the container sizes and types match with unassigned containers in the job.",
        "warning"
      );
    }
  };

  const handleEditContainer = (row: Record<string, any>) => {
    console.log("Row:", row);

    setEditContainerData({
      jobBreakupDetailId: row.jobBreakupDetailId,
      containerNumber: row.containerNumber || "",
      containerSize: row.containerSize || "",
      containerType: row.containerType || "",
      driverIdName: row.driverIdName || "N/A",
      truckIdName: row.truckIdName || "N/A",
      transporterIdName: row.transporterIdName || "N/A",
      jobCode: row.jobCode || "",
      source: row.source || "",
      destination: row.destination || "",
    });
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditContainerData(null);
  };

  // Function to get existing container numbers in the job
  const getExistingContainerNumbers = useCallback(() => {
    if (!jobOrder) return new Set<string>();

    const existingContainers = new Set<string>();
    jobOrder.jobBreakups.forEach((breakup) => {
      breakup.jobBreakUpDetails.forEach((detail) => {
        if (detail.containerNumber && detail.containerNumber !== "N/A") {
          existingContainers.add(detail.containerNumber.toUpperCase());
        }
      });
    });
    return existingContainers;
  }, [jobOrder]);

  const handleUpdateContainer = async () => {
    if (!editContainerData) return;

    try {
      loader.setLoading(true);
      const tripContainer: TripContainer = {
        tripId: editContainerData.jobBreakupDetailId,
        containerNumber: editContainerData.containerNumber,
      };

      const response = await JobService.assignContainers([tripContainer]);
      if (response.data.success) {
        showToast("Container updated successfully", "success");
        handleCloseEditModal();
        handleRefresh();
      } else {
        showToast(
          response.data.message || "Failed to update container",
          "error"
        );
      }
    } catch (error) {
      console.error("Error updating container:", error);
      showToast("Failed to update container", "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const handleTruckNumberClick = async (row: Record<string, any>) => {
    try {
      showLoader();
      setSelectedJobBreakupDetailId(row.jobBreakupDetailId);
      setIsTrackingModalOpen(true);
    } catch (err) {
      console.error("Error opening live tracking:", err);
    } finally {
      hideLoader();
    }
  };

  const handleTripIdClick = (row: Record<string, any>) => {
    // Get the current path segments
    const pathSegments = location.pathname.split("/");
    // Find the job status segment (all, initiated, etc.)
    const jobStatusIndex = pathSegments.indexOf("jobs") + 1;
    const jobStatus = pathSegments[jobStatusIndex];

    // Navigate to live tracking using the current job status
    navigate(
      `/jobs/${jobStatus}/${jobId}/${jobCode}/containers/${row.jobBreakupDetailId}/live-tracking`
    );
  };

  const handleJobCodeClick = () => {
    // Get the current path segments
    const pathSegments = location.pathname.split("/");
    // Find the job status segment (all, initiated, etc.)
    const jobStatusIndex = pathSegments.indexOf("jobs") + 1;
    const jobStatus = pathSegments[jobStatusIndex];

    // Navigate to job details using the current job status
    navigate(`/jobs/${jobStatus}/${jobId}/${jobCode}`);
  };

  const handleRemoveContainer = (row: Record<string, any>) => {
    setSelectedContainer(row);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedContainer) return;

    try {
      const tripContainer: TripContainer = {
        tripId: selectedContainer.jobBreakupDetailId,
        containerNumber: "",
      };

      const response = await JobService.assignContainers([tripContainer]);

      if (response.data.data.error) {
        showToast(response.data.data.error, "error");
      } else {
        showToast("Container number removed successfully", "success");
        handleRefresh();
      }
    } catch (error) {
      console.error("Error removing container:", error);
      showToast("Failed to remove container number", "error");
    } finally {
      setShowRemoveConfirm(false);
      setSelectedContainer(null);
    }
  };

  const handleForceEndTrip = useCallback(
    (row: Record<string, any>) => {
      setSelectedTripToEnd({
        jobBreakupDetailId: row.jobBreakupDetailId,
        jobCode: row.jobCode,
        containerNumber: row.containerNumber,
        containerSiTy: row.containerSiTy,
        truckIdName: row.truckIdName,
        driverIdName: row.driverIdName,
        transporterIdName: row.transporterIdName,
        pickedAt: row.pickedAt,
        tripStatus: row.tripStatus,
        source: jobOrder?.source || "N/A",
        destination: jobOrder?.destination || "N/A",
      });
      setIsForceEndTripModalOpen(true);
    },
    [jobOrder]
  );

  const handleCloseForceEndTripModal = useCallback(() => {
    setIsForceEndTripModalOpen(false);
    setSelectedTripToEnd(null);
  }, []);

  const handleEndTripSuccess = useCallback(async () => {
    await fetchJobDetails(true);
  }, [fetchJobDetails]);

  const handleEditTruck = (row: Record<string, any>) => {
    console.log("Edit Truck Row:", row);

    // Find the original job breakup detail to get driver information and raw truck number
    let originalDetail: any = null;
    for (const breakup of jobOrder?.jobBreakups || []) {
      for (const detail of breakup.jobBreakUpDetails) {
        if (detail.jobBreakupDetailId === row.jobBreakupDetailId) {
          originalDetail = detail;
          break;
        }
      }
      if (originalDetail) break;
    }

    if (!originalDetail) {
      showToast("Trip not found", "error");
      return;
    }

    // Check if truck number is available
    if (!originalDetail.truckIdName?.name) {
      showToast("No truck assigned to this trip", "error");
      return;
    }

    setEditTruckData({
      jobBreakupDetailId: row.jobBreakupDetailId,
      currentTruckNumber: originalDetail.truckIdName.name, // Use non-formatted truck number
      driverId: originalDetail.driverIdName?.id || 0, // Get driver ID from original detail
      transporterId: originalDetail.transporterIdName?.id || 0, // Use transporter ID for safer filtering
      driverName: originalDetail.driverIdName?.name || "N/A", // Add driver name
      transporterName: originalDetail.transporterIdName?.name || "N/A", // Add transporter name
    });
    setIsTruckModalOpen(true);
  };

  const handleCloseTruckModal = () => {
    setIsTruckModalOpen(false);
    setEditTruckData(null);
  };

  const actions = [
    {
      label: "Update Container Number",
      callback: handleEditContainer,
      isEnabled: (row: Record<string, any>) => {
        return row?.containerNumber !== null && row.containerNumber !== "N/A";
      },
    },
    // {
    //   label: "Remove Container No.",
    //   callback: handleRemoveContainer,
    //   isEnabled: (row: Record<string, any>) =>
    //     row?.containerNumber !== null &&
    //     row?.tripStatus !== "IN_TRANSIT" &&
    //     row?.tripStatus !== "DROPPED",
    // },
    {
      label: "Update Truck Number",
      callback: handleEditTruck,
      isEnabled: (row: Record<string, any>) => {
        // Find the original trip data to check if truck is assigned
        const trip = containerData.find(
          (trip) => trip.jobBreakupDetailId === row.jobBreakupDetailId
        );
        return (
          !!(trip?.truckIdName && trip.truckIdName !== "N/A") &&
          (row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED")
        );
      },
    },
    {
      label: "Force End Trip",
      callback: handleForceEndTrip,
      isEnabled: (row: Record<string, any>) =>
        row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED",
    },
    {
      label: "Live Tracking",
      callback: handleTruckNumberClick,
      isEnabled: (row: Record<string, any>) =>
        (row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED") &&
        row?.truckIdName !== "N/A",
    },
    // {
    //   label: "Transfer Trip",
    //   callback: (row: Record<string, any>) => {
    //     console.log("Trip transfer clicked for row:", row);
    //   },
    //   isEnabled: (row: Record<string, any>) =>
    //     row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED",
    // },
    // {
    //   label: "View Images",
    //   callback: () => {
    //     console.log("View Images");
    //   },
    //   isEnabled: (row: Record<string, any>) => false,
    // },
    // {
    //   label: "View Documents",
    //   callback: () => {
    //     console.log("View Documents");
    //   },
    //   isEnabled: (row: Record<string, any>) => false,
    // },
  ];

  // Define columns for the table
  const columns: TableColumn[] = [
    {
      header: "Container No.",
      accessor: "containerNumber",
      sortable: false,
      uppercase: true,
      width: "140px",
    },
    {
      header: "Size & Type",
      accessor: "containerSiTy",
      sortable: true,
      centerAlign: true,
      width: "120px",
      cell: (value: any, row: any) => {
        if (!row || !row.containerSize || !row.containerType) {
          return row?.containerSiTy;
        }

        const size = containerSizeStringToNumber(row.containerSize);
        if (size === null) {
          return row.containerSiTy;
        }

        return <ContainerType size={size} type={row.containerType} />;
      },
    },
    {
      header: "Trip Id.",
      accessor: "jobBreakupDetailId",
      sortable: true,
      centerAlign: true,
      width: "100px",
      cell: (value: any, row: any) => {
        const isTrackable =
          row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED";
        const content = isTrackable ? (
          <span
            className="text-[#027CB5] hover:underline cursor-pointer"
            onClick={() =>
              navigate(
                `/jobs/all/${row.jobId}/${row.jobCode}/containers/${row.jobBreakupDetailId}/live-tracking`
              )
            }
          >
            {value}
          </span>
        ) : (
          <span>{value}</span>
        );
        return content;
      },
    },
    {
      header: "Truck Number",
      accessor: "truckIdName",
      sortable: true,
      width: "150px",
      cell: (value: string, row: Record<string, any>) => {
        const isTrackable =
          row?.tripStatus === "IN_TRANSIT" || row?.tripStatus === "PICKED";
        const hasTruck = row?.truckIdName && row?.truckIdName !== "N/A";

        if (isTrackable && hasTruck) {
          return (
            <span
              className="text-[#027CB5] hover:underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleTruckNumberClick(row);
              }}
            >
              {value}
            </span>
          );
        }

        return <NAValue value={value} />;
      },
    },
    {
      header: "Driver Name",
      accessor: "driverIdName",
      sortable: true,
      cell: (value: string) => <NAValue value={value} />,
      // width: "120px",
    },
    {
      header: "Transporter Name",
      accessor: "transporterIdName",
      sortable: true,
      cell: (value: string) => <NAValue value={value} />,
    },
    {
      header: "Picked at",
      accessor: "pickedAt",
      sortable: true,
      cell: (value: string) => <NAValue value={value} />,
    },
    {
      header: "Dropped at",
      accessor: "droppedAt",
      sortable: true,
      cell: (value: string) => <NAValue value={value} />,
    },
    {
      header: "Status",
      accessor: "tripStatus",
      centerAlign: true,
      sortable: true,
      width: "120px",
      cell: (_value: any, row: Record<string, any>) => (
        <ContainerStatus container={row as JobBreakUpDetail} />
      ),
    },
  ];

  const options = [
    ...(jobOrder?.jobStatus === JobStatusEnum.INITIATED ||
    jobOrder?.jobStatus === JobStatusEnum.ASSIGNED
      ? [
          { label: "Edit", value: "edit", icon: <EditIcon /> },
          { label: "Delete", value: "delete", icon: <DeleteIcon /> },
          { label: "Cancel", value: "cancel", icon: <CancelIcon /> },
        ]
      : jobOrder?.jobStatus === JobStatusEnum.CANCELLED
      ? [{ label: "Delete", value: "delete", icon: <DeleteIcon /> }]
      : jobOrder?.jobStatus === JobStatusEnum.STARTED
      ? [{ label: "Edit", value: "edit", icon: <EditIcon /> }]
      : []),
  ];

  const handleDispatchOpen = () => setOpenDispatchConfirm(true);
  const handleDispatchClose = () => setOpenDispatchConfirm(false);
  const handleDispatchConfirm = async () => {
    setOpenDispatchConfirm(false);
    if (!jobOrder?.jobId) return;
    try {
      loader.setLoading(true);
      const jobId = parseInt(jobOrder.jobId);
      const dispatchJobRes = await JobService.dispatchJob(jobId);
      if (dispatchJobRes.data.error) {
        showToast(`Failed to dispatch job !`, "error");
      } else {
        handleRefresh();
        showToast(`Job ${jobOrder?.jobCode} has been dispatched !`, "success");
      }
    } catch (error) {
      console.error("Failed to dispatch job", error);
      showToast(`Failed to dispatch job !`, "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const handleJobStatusChangeClose = () => setOpenJobStatusChangeConfirm(false);

  const handleJobStatusChangeConfirm = async () => {
    setOpenJobStatusChangeConfirm(false);
    if (!jobOrder?.jobId) return;
    try {
      loader.setLoading(true);
      const jobId = parseInt(jobOrder.jobId);
      if (newJobStatus === "delete") {
        const deleteJobRes = await JobService.deleteJob(jobId);
        if (deleteJobRes.data.error) {
          showToast(`Failed to delete job !`, "error");
        } else {
          navigate(-1);
          showToast(`Job ${jobOrder?.jobCode} has been deleted !`, "success");
        }
      } else if (newJobStatus === "cancel") {
        const cancelJobRes = await JobService.cancelJob(jobId);
        if (cancelJobRes.data.error) {
          showToast(`Failed to cancel job !`, "error");
        } else {
          handleRefresh();
          showToast(`Job ${jobOrder?.jobCode} has been cancelled !`, "success");
        }
      }
    } catch (error) {
      console.error("Failed to update job status", error);
      showToast(`Failed to update job status !`, "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const handleJobAction = (action: string) => {
    switch (action) {
      case "edit":
        navigate("/jobs/edit/" + jobOrder?.jobId + "/" + jobOrder?.jobCode);
        break;
      case "delete":
        setNewJobStatus("delete");
        setJobStatusChangeTitle("Delete Job Confirmation");
        setOpenJobStatusChangeConfirm(true);
        break;
      case "cancel":
        setNewJobStatus("cancel");
        setJobStatusChangeTitle("Cancel Job Confirmation");
        setOpenJobStatusChangeConfirm(true);
        break;
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h2>Error Loading Job</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={() => fetchJobDetails()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show no data state
  if (!loading && !jobOrder) {
    return (
      <div className="no-data-container">
        <div className="no-data">
          <h2>No Job Data Available</h2>
          <p>The requested job could not be found or has no data.</p>
          <button className="back-button" onClick={handleBackClick}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Add a null check to ensure jobOrder is not null before rendering
  if (!jobOrder) {
    return null;
  }

  return (
    <div className="view-job-container">
      <div className="tabs-section">
        <div className="tabs-header flex flex-row justify-between items-center">
          <div className="left flex flex-row">
            <button
              className={`tab-button ${
                activeTab === "details" ? "active" : ""
              }`}
              onClick={() => handleTabChange("details")}
            >
              <span>Job Details</span>
            </button>
            <button
              className={`tab-button ${
                activeTab === "containers" ? "active" : ""
              }`}
              onClick={() => handleTabChange("containers")}
            >
              <span>Containers</span>
            </button>
          </div>
          <div className="right flex items-center gap-[10px]">
            {/* {!jobOrder?.availableToDrivers &&
              jobOrder?.jobStatus !== JobStatusEnum.COMPLETED &&
              jobOrder?.jobStatus !== JobStatusEnum.DELETED &&
              jobOrder?.jobStatus !== JobStatusEnum.EDITED &&
              jobOrder?.jobStatus !== JobStatusEnum.CANCELLED && (
                <button
                  className="dispatch_button"
                  type="button"
                  onClick={handleDispatchOpen}
                  style={{
                    backgroundColor: "#01ba65",
                    color: "#fff",
                    textTransform: "capitalize",
                    minWidth: "31px",
                    fontSize: "16px",
                    borderColor: "#01ba65",
                    borderRadius: "5px",
                    padding: "8px 16px",
                  }}
                >
                  Dispatch
                </button>
              )}

            <ButtonDropdown
              label="Actions"
              options={options}
              onSelect={(e: string) => handleJobAction(e)}
              disabled={
                jobOrder?.jobStatus === JobStatusEnum.COMPLETED ||
                jobOrder?.jobStatus === JobStatusEnum.DELETED ||
                jobOrder?.jobStatus === JobStatusEnum.EDITED
              }
            /> */}
            <JobCompletionStatus job={jobOrder} />
          </div>
        </div>

        <div className="tab-content">
          {activeTab === "details" && (
            <div className="job-details overflow-hidden">
              <div className="flex flex-row">
                <div className="job-details-section flex-1">
                  <JobDetails job={jobOrder} />
                </div>
                <div className="transporter-section flex-1">
                  <TransporterDetails jobOrder={jobOrder} />
                </div>
              </div>
            </div>
          )}

          {activeTab === "containers" && (
            <div className="containers-section p-[20px]">
              <div className="stats">
                <StatsBoxContainer
                  stats={[
                    {
                      name: "Total",
                      value: containerStats.totalContainer,
                      valueColor: "#59585B",
                    },
                    {
                      name: "Available",
                      value: containerStats.totalUnpickedContainers,
                      valueColor: "#1976d2",
                    },
                    {
                      name: "Pending",
                      value: containerStats.pendingToAssign,
                      valueColor: "#7b1fa2",
                    },
                    {
                      name: "Picked",
                      value: containerStats.totalPickedContainers,
                      valueColor: "#f57c00",
                    },
                    {
                      name: "Dropped",
                      value: containerStats.totalDroppedContainers,
                      valueColor: "#2e7d32",
                    },
                  ]}
                />
              </div>

              <div className="flex justify-between items-center py-[20px]">
                <div className="mt-2">
                  <SearchBar
                    placeholder="Search"
                    onSearch={setSearchQuery}
                    showClearButton={true}
                    initialQuery={searchQuery}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-2">
                    {containerStats && containerStats.pendingToAssign > 0 && (
                      <>
                        <button
                          className="assign_containers_button"
                          type="button"
                          onClick={handleAssignContainers}
                        >
                          Assign Container Nos.
                        </button>

                        <button
                          className="excel_import_button"
                          type="button"
                          onClick={handleOpenImportContainerDataModal}
                        >
                          Import from Excel File
                        </button>

                        <button
                          className="excel_download_button"
                          type="button"
                          onClick={downloadExcelTemplate}
                        >
                          Download Template
                        </button>
                      </>
                    )}

                    <button
                      type="button"
                      className="refresh_button"
                      onClick={handleRefresh}
                      disabled={loader.isLoading}
                    >
                      <div className="flex items-center">Refresh</div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="table">
                <TableComponent
                  showPagination={true}
                  columns={columns}
                  data={filteredContainerData}
                  showCheckboxes={false}
                  highlightSearchMatches={true}
                  searchQuery={searchQuery}
                  pageSize={pageSize}
                  pageSizeOptions={pageSizeOptions}
                  currentPage={currentPage}
                  totalPages={getTotalPages()}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  showSerialNumbers={true}
                  actions={actions}
                  enableRowHover={true}
                  isServerSidePagination={false}
                  totalElements={filteredContainerData.length}
                  enableClientSideFiltering={true}
                  defaultSort={{ column: "pickedAt", direction: "asc" }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {jobOrder && (
        <Modal
          className="component_modal_container assign_containers_modal"
          title="Assign Containers"
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          footer={
            <div className="flex justify-center gap-4">
              <button
                type="button"
                onClick={() => setIsAssignModalOpen(false)}
                className="secondary_button w-[128px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // This will be handled by the JobBreakupTable's handleSave
                  // We need to trigger it from the parent
                }}
                className="primary_button w-[128px]"
              >
                Save
              </button>
            </div>
          }
        >
          <JobBreakupTable
            job={jobOrder}
            onSave={handleUpdatedJob}
            onCancel={() => setIsAssignModalOpen(false)}
          />
        </Modal>
      )}

      <Modal
        isOpen={isImportContainerModalOpen}
        onClose={handleCloseImportContainerDataModal}
        title={`Import from Excel File`}
        className="import_containers_modal"
      >
        <div className="import_excel_component">
          <div className="upload_area">
            <input
              type="file"
              onChange={readSelectedExcel}
              accept=".xlsx, .xls, .csv"
              className="w-full h-[100px] border border-[#CBD2D9] rounded-[5px] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20744a] file:text-white hover:file:bg-[#185c3d]"
              disabled={loader.isLoading}
            />
          </div>

          {uploadedJobCode && (
            <div className="flex justify-center p-[10px]">
              <p
                className={`text-sm font-medium ${
                  uploadedJobCode ===
                  jobCode?.trim().replace(/#/g, "").toUpperCase()
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {uploadedJobCode ===
                jobCode?.trim().replace(/#/g, "").toUpperCase()
                  ? "✔ "
                  : "✘ "}
                {uploadedJobCode}
              </p>
            </div>
          )}

          {uploadedData.length > 0 ? (
            <div className="overflow-auto max-h-80 border rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Sl. No.
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Container Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {uploadedData.map((item, index) => {
                    const containerNumber = item.containerNumber.toUpperCase();
                    const isExisting =
                      existingContainerNumbers.has(containerNumber);
                    const isDuplicate =
                      duplicateContainerNumbers.has(containerNumber);
                    const isValidFormat = /^[A-Z]{4}\d{7}$/.test(
                      containerNumber
                    );

                    let status = "New";
                    let statusColor = "text-green-600";

                    if (!isValidFormat) {
                      status = "Invalid Format";
                      statusColor = "text-red-600";
                    } else if (isExisting) {
                      status = "Already in Job";
                      statusColor = "text-red-600";
                    } else if (isDuplicate) {
                      status = "Duplicate in Upload";
                      statusColor = "text-orange-600";
                    }

                    return (
                      <tr key={index}>
                        <td className="px-6 py-4 text-sm text-gray-700 text-center">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {containerNumber}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.containerSize}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {item.containerType}
                        </td>
                        <td
                          className={`px-6 py-4 text-sm font-medium ${statusColor}`}
                        >
                          {status}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 mt-2 text-center">
              Please upload an Excel file (.xlsx) to import container data
            </p>
          )}

          <div className="w-full flex justify-center mt-[20px] gap-[20px]">
            <button
              type="button"
              onClick={handleCloseImportContainerDataModal}
              className="secondary_button w-[128px]"
              disabled={loader.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                !isIntegrateButtonEnabled() ||
                loader.isLoading ||
                validUploadedData.length === 0
              }
              className="primary_button w-[128px]"
            >
              Import
            </button>
          </div>
        </div>
      </Modal>

      <UpdateContainerModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        containerData={editContainerData}
        onUpdateSuccess={handleRefresh}
        existingContainerNumbers={getExistingContainerNumbers()}
      />

      {jobOrder && selectedJobBreakupDetailId && (
        <Modal
          size="full"
          className="live-tracking-modal"
          title="Live Tracking"
          isOpen={isTrackingModalOpen}
          onClose={() => setIsTrackingModalOpen(false)}
        >
          <LiveTracking
            job={jobOrder}
            jobBreakupDetailId={selectedJobBreakupDetailId}
          />
        </Modal>
      )}

      <ConfirmDialog
        open={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false);
          setSelectedContainer(null);
        }}
        onConfirm={handleConfirmRemove}
        title="Remove Container Number"
        message={`Are you sure you want to remove the container number ${selectedContainer?.containerNumber}?`}
        confirmText="Yes"
        confirmButtonStyle={{
          backgroundColor: "#ff0000",
          color: "#fff",
        }}
      />

      <ConfirmDialog
        open={openDispatchConfirm}
        onClose={handleDispatchClose}
        onConfirm={handleDispatchConfirm}
        title="Dispatch Job Confirmation"
        message={`Are you sure you want to Dispatch the Job #${jobOrder?.jobCode} ?`}
        confirmText="Yes"
        confirmButtonStyle={{
          backgroundColor: "#01ba65",
          color: "#fff",
        }}
      />

      <ConfirmDialog
        open={openJobStatusChangeConfirm}
        onClose={handleJobStatusChangeClose}
        onConfirm={handleJobStatusChangeConfirm}
        title={jobStatusChangeTitle}
        message={`Are you sure you want to ${newJobStatus} the Job #${jobOrder?.jobCode} ?`}
        confirmText="Yes"
        confirmButtonStyle={{
          backgroundColor: "#ff0000",
          color: "#fff",
        }}
      />

      <UpdateTruckModal
        isOpen={isTruckModalOpen}
        onClose={handleCloseTruckModal}
        truckData={editTruckData}
        onUpdateSuccess={async () => {
          handleCloseTruckModal();
          await fetchJobDetails(true);
        }}
      />

      <ForceEndTripModal
        isOpen={isForceEndTripModalOpen}
        onClose={handleCloseForceEndTripModal}
        tripData={selectedTripToEnd}
        onEndTripSuccess={handleEndTripSuccess}
      />
    </div>
  );
};

export default ViewJob;
